import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { noop } from '@app/core/utils/rxjs';
import { SuperHttpClientProvider } from '@paella-front/ngx-super-http-client';
import { BehaviorSubject, Observable, from, iif, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { OauthService, Token } from '../services/oauth.service';

@Injectable()
export class OauthHttpInterceptor implements HttpInterceptor {
  private inProgress$ = new BehaviorSubject(false);

  constructor(
    private router: Router,
    private $oauth: OauthService,
    private $$shttp: SuperHttpClientProvider
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((reason: any) => {
        if (
          reason instanceof HttpErrorResponse &&
          !this.$oauth.matches(request.url) &&
          reason.status === 401
        ) {
          if (this.inProgress$.getValue()) {
            /// If inProgress$ is true we will wait until is false
            return this.inProgress$.pipe(
              filter((progress: boolean) => !progress),
              take(1),
              this.refreshToken(request, next)
            );
          }
          /// Set the inProgress$ to true so subsequent API calls will wait
          this.inProgress$.next(true);

          /// Call $auth.refresh()
          return this.$oauth.refresh().pipe(
            this.refreshToken(request, next),
            catchError((error: Error) =>
              from(this.$oauth.signout()).pipe(
                catchError((_error: Error) => {
                  console.error('Tried to force signout but an Error thrown:', _error);
                  return of(null);
                }),
                tap(() => {
                  this.inProgress$.next(false);
                  this.router.navigate(['/', 'redirect']);
                }),
                mergeMap(() => throwError(() => error))
              )
            )
          );
        }

        return throwError(() => reason);
      })
    );
  }

  private refreshToken<T>(
    request: HttpRequest<any>,
    next: HttpHandler
  ): (source: Observable<T>) => Observable<HttpEvent<T>> {
    return (source: Observable<T>) =>
      source.pipe(
        mergeMap(() =>
          from(this.$oauth.getToken()).pipe(
            mergeMap((token: Token) => iif(() => !!token, noop(), of(request)))
          )
        ),
        tap(() => this.inProgress$.next(false)),
        switchMap(() =>
          next.handle(
            request.clone({ setHeaders: this.$$shttp.use('platform').headers.getAll() })
          )
        )
      );
  }
}
